/**
 * PixiTrailRenderer — movement trails behind every live player.
 *
 * Each frame: sample every visible player's *rendered* position into
 * TrailHistory, prune old points, then let the player's trail style draw the
 * ring into a per-player Graphics on the `trails` layer (below entities, so
 * the trail sits under the sprite). The Graphics are redrawn every frame on
 * purpose — a trail changes every frame; the dirty-flag rule is for HUD
 * elements that do not.
 *
 * Decoys trail too, and through this same path: a clone with no trail behind it
 * would give itself away at a glance.
 *
 * Colour: team colour in team modes (the trail then doubles as a team marker
 * at a glance), otherwise the trail definition's colour. Invisible remote
 * players leave no trail; the local player keeps their own at full strength.
 */
import * as PIXI from 'pixi.js';
import { TrailHistory, samplesOutsideBody } from '../../core/TrailHistory.js';
import { GameState } from '../../core/GameState.js';
import { resolveAbilityView } from './abilityEffects.js';
import { getTrailStyle, resolveTrailDefinition, toColor } from './trailStyles.js';

/**
 * While a player drifts (hold-to-slide) their trail flares: wider, brighter,
 * longer-lived. The slide is the game's signature move, so the trail is how
 * everyone else reads it — no extra renderer, the same style with hotter numbers.
 */
export const DRIFT_TRAIL = Object.freeze({ widthScale: 1.8, alphaMin: 0.9, lifeScale: 1.5 });

const PixiTrailRenderer = {
  container: null,
  history: null,
  /** @type {Map<string, PIXI.Graphics>} */
  graphics: new Map(),
  enabled: true,

  init(container) {
    this.container = container;
    this.history = new TrailHistory();
    this.graphics = new Map();
  },

  /**
   * @param {number} renderTime - the interpolation time used for entities
   * @param {number} now - performance.now()
   * @param {object} gameState
   */
  update(renderTime, now, gameState) {
    if (!this.container || !this.history) return;
    if (!this.enabled) { this.reset(); return; }

    if (typeof gameState.drainTrailBreaks === 'function') {
      for (const id of gameState.drainTrailBreaks()) this.history.remove(id);
    }

    const alive = new Set();
    /** @type {Map<string, {x: number, y: number}>} bodies drawn see-through this frame */
    const seeThrough = new Map();
    // Decoys are never local and never drifting, so they simply take the owner's
    // ordinary trail — which is the point.
    const entities = [...gameState.players.values(), ...(gameState.decoys?.values?.() ?? [])];
    for (const player of entities) {
      if (player.isDead) continue;
      const view = resolveAbilityView(player);
      const rs = player.getRenderState(renderTime);
      // Invisible remote players leave no trail: it would give them away. The
      // local player's view is only faded, not hidden, so they keep their own
      // trail. record() drops the track of anyone not visible.
      const visible = view.visible;
      this.history.record(player.id, rs.x, rs.y, now, visible);
      if (visible) alive.add(player.id);
      // A see-through body (the local invisible view) would show the trail
      // drawn beneath it, so that part of the trail is left out below.
      if (visible && view.alpha < 1) seeThrough.set(player.id, rs);
    }
    this.history.retainOnly(alive);
    this.history.prune(now);

    for (const id of alive) {
      let samples = this.history.samplesFor(id);
      const body = seeThrough.get(id);
      if (body) samples = samplesOutsideBody(samples, body.x, body.y, GameState.MOVEMENT.radius);
      const player = gameState.players.get(id) ?? gameState.decoys?.get?.(id);
      if (!player) continue;
      let g = this.graphics.get(id);
      if (!g) { g = new PIXI.Graphics(); this.container.addChild(g); this.graphics.set(id, g); }
      g.clear();
      if (samples.length < 2) continue;

      const def = resolveTrailDefinition(player, gameState.cosmeticsConfig);
      const teamColor = typeof gameState.teamColor === 'function' ? gameState.teamColor(player.team) : null;
      const drifting = !!player.drift?.active;
      getTrailStyle(def.style).draw(g, samples, {
        now,
        lifeMs: drifting ? def.lifeMs * DRIFT_TRAIL.lifeScale : def.lifeMs,
        color: teamColor ?? toColor(def.color),
        secondaryColor: def.secondaryColor != null ? toColor(def.secondaryColor) : null,
        width: drifting ? def.width * DRIFT_TRAIL.widthScale : def.width,
        alpha: drifting ? Math.max(def.alpha, DRIFT_TRAIL.alphaMin) : def.alpha,
        isLocal: !!player.isLocal
      });
    }

    for (const [id, g] of this.graphics) {
      if (!alive.has(id)) { this.container.removeChild(g); g.destroy(); this.graphics.delete(id); }
    }
  },

  /** A teleport or respawn must not draw a line across the map. */
  forget(playerId) {
    this.history?.remove(playerId);
  },

  reset() {
    this.history?.clear();
    for (const g of this.graphics.values()) { this.container?.removeChild(g); g.destroy(); }
    this.graphics.clear();
  },

  destroy() {
    this.reset();
    this.container = null;
    this.history = null;
  }
};

export { PixiTrailRenderer };
